import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Clock, AlertTriangle } from 'lucide-react';

function useCountdown(deadline) {
  const [remaining, setRemaining] = useState(() => {
    if (!deadline) return null;
    return new Date(deadline).getTime() - Date.now();
  });
  useEffect(() => {
    if (!deadline) return;
    const tick = () => setRemaining(new Date(deadline).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return remaining;
}

export function CountdownBadge({ deadline, penaltyVnd, showPenalty = true, t }) {
  const remaining = useCountdown(deadline);
  if (remaining === null) return null;

  const tr = (key, fallback) => (t ? t(key) : fallback);

  if (remaining <= 0) {
    const penalty = penaltyVnd != null ? penaltyVnd : Math.ceil(Math.abs(remaining) / 86400000) * 10000;
    const overdueDays = penalty > 0 ? penalty / 10000 : 1;
    return (
      <div className="flex items-center gap-1.5" data-testid="countdown-overdue">
        <AlertTriangle size={14} className="text-red-600 animate-pulse" />
        <span className="text-xs font-bold text-red-600">{tr('countdown_late', 'RETARD')} {overdueDays}{tr('countdown_day_suffix', 'j')}</span>
        {showPenalty && (
          <Badge className="bg-red-100 text-red-700 text-xs font-bold">-{penalty.toLocaleString()} VND</Badge>
        )}
      </div>
    );
  }

  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const isUrgent = remaining < 3600000;

  return (
    <div className={`flex items-center gap-1.5 ${isUrgent ? 'animate-pulse' : ''}`} data-testid="countdown-active">
      <Clock size={14} className={isUrgent ? 'text-red-500' : 'text-amber-600'} />
      <span className={`text-xs font-bold ${isUrgent ? 'text-red-600' : 'text-amber-800'}`}>
        {tr('countdown_remaining', 'Il reste')} {hours}h {String(minutes).padStart(2, '0')}min
      </span>
    </div>
  );
}
